import {
  adaptCustomerLanguagePreference,
  adaptCustomerTranslationPage,
  adaptCustomerLanguageNotification,
  adaptCustomerTranslationNotification,
  AGENT_SERVICE_SUPPORTED_LANGUAGES,
} from "./agent-service-language.js";

/** 订单/总客服共用的语言会话；无全局记忆、无本地伪持久化，也不创建聊天消息。 */
export const createAgentServiceLanguageSession = ({ api, onChange }) => {
  let context = null;
  let epoch = 0;
  let refreshing = false;
  let queuedMessages = null;
  let absentIds = new Set();
  let state = {
    ready: false,
    changing: false,
    preference: null,
    translations: {},
    notice: "",
  };
  const publish = (patch) => {
    state = { ...state, ...patch };
    onChange(state);
  };
  const validPreference = (value) => {
    const parsed = adaptCustomerLanguagePreference(value);
    if (
      parsed.conversationId !== context.conversationId ||
      parsed.tenantId !== context.tenantId
    )
      throw new Error("语言回执不属于当前会话。");
    return parsed;
  };
  const acceptPreference = (value) => {
    const parsed = validPreference(value);
    if (
      state.preference &&
      parsed.languageVersion === state.preference.languageVersion &&
      parsed.customerLanguage !== state.preference.customerLanguage
    )
      throw new Error("同一语言版本不可返回不同偏好。");
    if (
      !state.preference ||
      parsed.languageVersion >= state.preference.languageVersion
    )
      publish({ preference: parsed });
  };
  return {
    get state() {
      return state;
    },
    /** 关闭先失效所有异步请求；旧窗口的响应不能进入新会话。 */
    close() {
      epoch += 1;
      context = null;
      refreshing = false;
      queuedMessages = null;
      absentIds = new Set();
      publish({
        ready: false,
        changing: false,
        preference: null,
        translations: {},
        notice: "",
      });
    },
    async open(nextContext) {
      this.close();
      if (
        !nextContext?.conversationId ||
        !nextContext?.tenantId ||
        !["CUSTOMER", "BUSINESS_STAFF", "SUPPORT_STAFF", "STAFF"].includes(
          nextContext.participantType,
        )
      )
        return;
      context = { ...nextContext };
      const current = epoch;
      try {
        const result = await api.read(context.conversationId);
        if (current !== epoch) return;
        acceptPreference(result);
        publish({ ready: true });
      } catch (error) {
        if (current !== epoch) return;
        publish({
          notice:
            error.response?.status === 404
              ? "多语言接口尚未接通，原文聊天不受影响。"
              : "语言设置读取失败，原文聊天不受影响。",
        });
      }
    },
    async change(customerLanguage) {
      if (
        !state.ready ||
        state.changing ||
        context?.participantType !== "CUSTOMER" ||
        !AGENT_SERVICE_SUPPORTED_LANGUAGES.includes(customerLanguage) ||
        customerLanguage === state.preference.customerLanguage
      )
        return;
      const current = epoch;
      const expectedLanguageVersion = state.preference.languageVersion;
      publish({ changing: true, notice: "" });
      try {
        const result = await api.update(context.conversationId, {
          customerLanguage,
          expectedLanguageVersion,
        });
        if (current !== epoch) return;
        const parsed = validPreference(result);
        if (
          parsed.customerLanguage !== customerLanguage ||
          parsed.languageVersion !== expectedLanguageVersion + 1
        )
          throw new Error("语言更新回执不匹配。");
        acceptPreference(parsed);
      } catch {
        if (current === epoch)
          publish({ notice: "语言切换失败，仍使用上次保存的语言，请重试。" });
      } finally {
        if (current === epoch) {
          publish({ changing: false });
          if (queuedMessages && !refreshing) {
            const messages = queuedMessages;
            queuedMessages = null;
            await this.refresh(messages);
          }
        }
      }
    },
    /** 实时事件只唤醒鉴权补读，不创建新消息、不增加未读数、不重译历史。 */
    async acceptNotification(value, messages = []) {
      const event =
        value.type === "message.translation.updated"
          ? adaptCustomerTranslationNotification(value)
          : adaptCustomerLanguageNotification(value);
      if (
        !state.ready ||
        event.conversationId !== context?.conversationId ||
        event.tenantId !== context?.tenantId
      )
        return;
      if (event.type === "message.translation.updated") {
        absentIds.delete(event.businessMessageId);
      }
      if (refreshing || state.changing) {
        queuedMessages = messages;
        return;
      }
      await this.refresh(messages);
    },
    /** 仅补读当前已加载消息的附加译文；没条目的旧消息不会触发补译。 */
    async refresh(messages = []) {
      if (!state.ready || refreshing || state.changing) return;
      const current = epoch;
      refreshing = true;
      try {
        const preference = await api.read(context.conversationId);
        if (current !== epoch) return;
        // 语言切换期间的旧GET只能读译文，不能覆盖新偏好。
        if (!state.changing) acceptPreference(preference);
        const ids = [
          ...new Set(
            messages
              .filter(
                (message) =>
                  message.actorType !== "SYSTEM" &&
                  !message.localStatus &&
                  typeof message.businessMessageId === "string" &&
                  message.businessMessageId.length > 0,
              )
              .map((message) => message.businessMessageId),
          ),
        ]
          .filter(
            (id) =>
              !absentIds.has(id) &&
              (!state.translations[id] ||
                state.translations[id].status === "PENDING"),
          )
          .slice(-50);
        if (!ids.length) return;
        const page = adaptCustomerTranslationPage(
          await api.translations(context.conversationId, ids),
        );
        if (current !== epoch) return;
        if (
          page.conversationId !== context.conversationId ||
          page.tenantId !== context.tenantId ||
          page.items.some((item) => !ids.includes(item.businessMessageId))
        )
          throw new Error("译文回执不属于本次补读。");
        const translations = { ...state.translations };
        page.items.forEach((item) => {
          translations[item.businessMessageId] = item;
        });
        ids
          .filter(
            (id) => !page.items.some((item) => item.businessMessageId === id),
          )
          .forEach((id) => absentIds.add(id));
        publish({ translations, notice: "" });
      } catch {
        if (current === epoch)
          publish({ notice: "译文更新暂时失败，原文仍可正常查看。" });
      } finally {
        if (current === epoch) {
          refreshing = false;
          if (queuedMessages && !state.changing) {
            const nextMessages = queuedMessages;
            queuedMessages = null;
            await this.refresh(nextMessages);
          }
        }
      }
    },
  };
};
